import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { getCategories } from '../../api';
import {
  FiShoppingCart, FiMenu, FiX, FiSearch, FiLogOut, FiSettings,
  FiPackage, FiChevronDown, FiTag, FiRefreshCw, FiPhone, FiMail, FiUser,
} from 'react-icons/fi';
import './Navbar.css';

const PRIMARY_LINKS = [
  { to: '/products', label: 'All Products' },
  { to: '/second-hand', label: 'Second Hand', accent: true },
  { to: '/blog', label: 'Blog' },
];

const Navbar = () => {
  const { user, logout, isAdmin } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [navCategories, setNavCategories] = useState([]);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 8);
    handler();
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    getCategories({ page_size: 10 })
      .then(({ data }) => setNavCategories(data.results || []))
      .catch(() => {});
  }, []);

  // Close transient UI on navigation
  useEffect(() => { setMobileOpen(false); setUserMenuOpen(false); }, [location]);

  // Close the account menu on an outside click
  useEffect(() => {
    if (!userMenuOpen) return undefined;
    const onClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [userMenuOpen]);

  // Lock body scroll behind the mobile drawer
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchQ.trim()) return;
    navigate(`/products?search=${encodeURIComponent(searchQ.trim())}`);
    setSearchQ('');
  };

  const handleLogout = () => { logout(); navigate('/'); };
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);
  const initial = (user?.first_name || user?.username || 'U')[0].toUpperCase();

  return (
    <header className={`nav ${scrolled ? 'is-scrolled' : ''}`}>
      {/* Utility bar */}
      <div className="nav-utility">
        <div className="container nav-utility-inner">
          <div className="nav-utility-left">
            <a href="tel:+9779705743261"><FiPhone /> +977 9705743261</a>
            <a href="mailto:info@djistorenepal.com" className="hide-sm"><FiMail /> info@djistorenepal.com</a>
          </div>
          <div className="nav-utility-right">
            <span className="nav-utility-note">Official DJI Authorized Dealer · Free delivery over NPR 50,000</span>
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div className="nav-main">
        <div className="container nav-main-inner">
          <button
            className="nav-icon-btn nav-hamburger"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <FiMenu />
          </button>

          <Link to="/" className="nav-logo" aria-label="DJI Store Nepal home">
            <span className="nav-logo-mark">DJI</span>
            <span className="nav-logo-text">Store Nepal</span>
          </Link>

          <form className="nav-search" onSubmit={handleSearch} role="search">
            <FiSearch className="nav-search-icon" />
            <input
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder="Search drones, cameras, accessories…"
              aria-label="Search products"
            />
            {searchQ && (
              <button type="button" className="nav-search-clear" onClick={() => setSearchQ('')} aria-label="Clear search">
                <FiX />
              </button>
            )}
          </form>

          <div className="nav-actions">
            <Link to="/cart" className="nav-icon-btn nav-cart" aria-label={`Cart, ${count} items`}>
              <FiShoppingCart />
              {count > 0 && <span className="nav-cart-badge">{count > 99 ? '99+' : count}</span>}
            </Link>

            {user ? (
              <div className="nav-account" ref={userMenuRef}>
                <button
                  className="nav-account-btn"
                  onClick={() => setUserMenuOpen((v) => !v)}
                  aria-expanded={userMenuOpen}
                  aria-haspopup="menu"
                >
                  <span className="nav-avatar">{initial}</span>
                  <span className="nav-account-name hide-md">{user.first_name || user.username}</span>
                  <FiChevronDown className={`nav-chevron ${userMenuOpen ? 'open' : ''}`} />
                </button>

                {userMenuOpen && (
                  <div className="nav-dropdown" role="menu">
                    <div className="nav-dropdown-head">
                      <strong>{user.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : user.username}</strong>
                      <small>{user.email}</small>
                    </div>
                    <Link to="/dashboard" className="nav-dropdown-item"><FiPackage /> My Orders</Link>
                    <Link to="/dashboard?tab=listings" className="nav-dropdown-item"><FiTag /> My Listings</Link>
                    <Link to="/second-hand/sell" className="nav-dropdown-item"><FiRefreshCw /> Sell Second Hand</Link>
                    <Link to="/dashboard?tab=profile" className="nav-dropdown-item"><FiUser /> Profile & Password</Link>
                    {isAdmin && (
                      <>
                        <div className="nav-dropdown-sep" />
                        <Link to="/admin" className="nav-dropdown-item accent"><FiSettings /> Admin Panel</Link>
                      </>
                    )}
                    <div className="nav-dropdown-sep" />
                    <button className="nav-dropdown-item danger" onClick={handleLogout}><FiLogOut /> Sign Out</button>
                  </div>
                )}
              </div>
            ) : (
              <div className="nav-auth">
                <Link to="/signin" className="btn btn-outline btn-sm hide-sm">Sign In</Link>
                <Link to="/signup" className="btn btn-primary btn-sm">Sign Up</Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Category strip */}
      <nav className="nav-links" aria-label="Categories">
        <div className="container nav-links-inner">
          <Link to="/" className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}>Home</Link>
          {PRIMARY_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`nav-link ${l.accent ? 'accent' : ''} ${isActive(l.to) ? 'active' : ''}`}
            >
              {l.label}
            </Link>
          ))}
          <span className="nav-links-sep" />
          {navCategories.slice(0, 6).map((cat) => (
            <Link key={cat.id} to={`/products?category=${cat.slug}`} className="nav-link">
              {cat.name}
            </Link>
          ))}
        </div>
      </nav>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div className="nav-scrim" onClick={() => setMobileOpen(false)} />
          <aside className="nav-drawer" aria-label="Menu">
            <div className="nav-drawer-head">
              <Link to="/" className="nav-logo">
                <span className="nav-logo-mark">DJI</span>
                <span className="nav-logo-text">Store Nepal</span>
              </Link>
              <button className="nav-icon-btn" onClick={() => setMobileOpen(false)} aria-label="Close menu">
                <FiX />
              </button>
            </div>

            <div className="nav-drawer-body">
              <Link to="/" className="nav-drawer-link">Home</Link>
              {PRIMARY_LINKS.map((l) => (
                <Link key={l.to} to={l.to} className={`nav-drawer-link ${l.accent ? 'accent' : ''}`}>
                  {l.label}
                </Link>
              ))}

              {navCategories.length > 0 && (
                <>
                  <div className="nav-drawer-label">Categories</div>
                  {navCategories.slice(0, 8).map((cat) => (
                    <Link key={cat.id} to={`/products?category=${cat.slug}`} className="nav-drawer-link sub">
                      {cat.name}
                    </Link>
                  ))}
                </>
              )}

              <div className="nav-drawer-label">Account</div>
              {user ? (
                <>
                  <Link to="/dashboard" className="nav-drawer-link sub">My Orders</Link>
                  <Link to="/dashboard?tab=listings" className="nav-drawer-link sub">My Listings</Link>
                  <Link to="/second-hand/sell" className="nav-drawer-link sub">Sell Second Hand</Link>
                  {isAdmin && <Link to="/admin" className="nav-drawer-link sub">Admin Panel</Link>}
                  <button className="nav-drawer-link sub danger" onClick={handleLogout}>Sign Out</button>
                </>
              ) : (
                <div className="nav-drawer-auth">
                  <Link to="/signin" className="btn btn-outline btn-block">Sign In</Link>
                  <Link to="/signup" className="btn btn-primary btn-block">Create Account</Link>
                </div>
              )}
            </div>
          </aside>
        </>
      )}
    </header>
  );
};

export default Navbar;
